import fp from 'lodash/fp';
import React, { useMemo, ComponentType, ComponentProps } from 'react';
import { getDateOr, formatCurrency } from '@factorin/util';
import { Registers } from '@factorin/domain';

import {
    Text,
    GradientPlaceholder,
    cn,
    FocusableDiv,
    SignedIcon,
    Forbidden,
    Check,
    Expired,
    Reply,
    Checkbox,
    FactorinCheckModel,
    stopPropagation,
} from 'Modules/ui';
import { paginationStatuses } from 'Modules/business/data';
import { useTranslation } from 'Modules/core/i18n';

import { DownloadButton } from '../DownloadButton';
import styles from './index.module.scss';
import {
    TemplateCodes,
    RowHeightsFunctionType,
    ItemProps,
    DiscountItemProps,
    ListItemType,
    DiscountListItemType,
    GetCheckableProps,
    ContractorField,
} from './types';


export const datesRegisterTemplateCodes: string[] = [
    'ReesterAccepted',
    'ReesterApproved',
    'ReesterCeded',
    'ReesterDiscount',
    'ReesterDiscountConf',
    'ReesterDiscountComp',
    'ReesterCompleted',
    'ReesterVerified',
    'ReesterFundedSupply',
];

const registerChildTemplateCodes: TemplateCodes = {
    ReesterCeded: [
        'ReesterFinanced',
        'ReesterNonFinanced',
    ],
    ReesterAccepted: [
        'ReesterConfirmed',
        'ReesterRejected',
    ],
    ReesterApproved: [],
    ReesterDiscount: [
        'ReesterDiscountConf',
        'ReesterDiscountRej',
    ],
    ReesterCompleted: [
        'ReesterCeded',
        'ReesterCompletedRej',
    ],
    ReesterFinanced: ['ReesterNoticeOfCess'],
};

const checkAvailableDates = (
    { RegisterTemplateCode, RegisterStartDate, RegisterEndDate }: Registers.Register,
): boolean =>
    datesRegisterTemplateCodes.includes(RegisterTemplateCode) &&
    !!RegisterStartDate &&
    !!RegisterEndDate;

const discountItemSizes = {
    child: 6.2,
    contractor: 10.6,
    default: 9.2,
};

const getDiscountRowHeights = ({ register, contractor, checkChild }) => {
    if (!register && contractor)
        return discountItemSizes.contractor;

    if (!register)
        return discountItemSizes.default;

    if (checkChild(register))
        return discountItemSizes.child;

    if (contractor)
        return discountItemSizes.contractor;

    return discountItemSizes.default;
};

const itemSizes = {
    default: 7.8,
    child: 5.8,
    date: 9.2,
    contractor: 1.4,
};

export const rowHeights = ({
    parentTemplateCode,
    contractor,
    contractors,
    customChildCheck,
    isDiscount,
}: RowHeightsFunctionType) => (register: Registers.Register) => {
    const checkChild = (element: Registers.Register) => customChildCheck
        ? customChildCheck(element)
        : parentTemplateCode &&
        registerChildTemplateCodes[parentTemplateCode].includes(element.RegisterTemplateCode);

    if (isDiscount)
        return getDiscountRowHeights({ register, contractor, checkChild });

    if (!register && contractors)
        return itemSizes.date + itemSizes.contractor * contractors;

    if (!register && contractor)
        return itemSizes.date + itemSizes.contractor;

    if (!register)
        return itemSizes.date;

    if (checkChild(register))
        return itemSizes.child;

    const isShowDates = checkAvailableDates(register);

    if (contractors && isShowDates)
        return itemSizes.date + itemSizes.contractor * contractors;

    if (contractors)
        return itemSizes.default + itemSizes.contractor * contractors;

    if (contractor && isShowDates)
        return itemSizes.date + itemSizes.contractor;

    if (contractor || isShowDates)
        return itemSizes.date;

    return itemSizes.default;
};

const getAmountField = (
    { AmountWithVat, Amount, CessionAmount, RegisterTemplateCode }: Registers.Register,
): string => {
    if (RegisterTemplateCode === 'ReesterFundedSupply') {
        if (CessionAmount)
            return `${formatCurrency(CessionAmount)} ₽`;

        return null;
    }

    if (AmountWithVat)
        return `${formatCurrency(AmountWithVat)} ₽`;

    if (Amount)
        return `${formatCurrency(Amount)} ₽`;

    return null;
};

const getSignIconKey = ({
    RegisterTemplateCode,
    FactorSignUid,
    DebtorORBuyerSignUid,
    ClientORProviderSignUid,
}: Registers.Register): string => {
    if (RegisterTemplateCode === 'ReesterCeded' &&
        FactorSignUid &&
        DebtorORBuyerSignUid &&
        ClientORProviderSignUid)
        return 'allSigned';

    if ((RegisterTemplateCode === 'ReesterApproved' ||
        RegisterTemplateCode === 'ReesterCeded') &&
        FactorSignUid)
        return 'signedByFactor';

    if (RegisterTemplateCode === 'ReesterDiscountConf' &&
        DebtorORBuyerSignUid &&
        ClientORProviderSignUid)
        return 'allSigned';

    if (RegisterTemplateCode === 'ReesterDiscountConf' &&
        ClientORProviderSignUid)
        return 'signedByClient';

    if (RegisterTemplateCode === 'ReesterFundedSupply' &&
        FactorSignUid &&
        ClientORProviderSignUid)
        return 'allSigned';

    if (RegisterTemplateCode === 'ReesterFinanced' &&
        FactorSignUid &&
        ClientORProviderSignUid)
        return 'allSigned';

    if (RegisterTemplateCode === 'ReesterNoticeOfCess' &&
        FactorSignUid &&
        ClientORProviderSignUid &&
        DebtorORBuyerSignUid)
        return 'allSigned';

    return null;
};

const getCheckedState = (
    value: any,
    { all, include = [], exclude = [] },
): boolean =>
    all && !exclude.includes(value) || include.includes(value);

const updateCheckedState = (
    value: any,
    { all, include = [], exclude = [] },
    checked: boolean,
): FactorinCheckModel => ({
    all,
    include: !all
        ? checked
            ? [...include, value]
            : include.filter(i => value !== i)
        : include,
    exclude: all
        ? checked
            ? exclude.filter(i => value !== i)
            : [...exclude, value]
        : exclude,
});

const getCheckedProps = ({
    register,
    checkable,
    checkedRows,
    checkedKeySelector,
    onCheck,
}: GetCheckableProps) => {
    if (checkable && (!fp.isFunction(checkable) || checkable(register))) {
        const checkedKey = checkedKeySelector(register);
        return {
            checkable: true,
            checked: getCheckedState(checkedKey, checkedRows),
            onCheck: (e: React.ChangeEvent<HTMLInputElement>) => onCheck(updateCheckedState(
                checkedKey,
                checkedRows,
                e.target.checked,
            )),
        };
    }

    return {};
};

const ForbiddenElement = ({ forChild }) => (
    <div className={cn(
        styles.rightIcon,
        styles.lightIcon,
        styles.statusIcon,
        forChild && styles.forChild,
    )}
    >
        <Forbidden />
    </div>
);

const ReplyingElement = ({ forChild }) => (
    <div className={cn(
        styles.rightIcon,
        styles.lightIcon,
        styles.statusIcon,
        forChild && styles.forChild,
    )}
    >
        <Reply />
    </div>
);

const ProcessedElement = ({ forChild }) => (
    <div className={cn(
        styles.rightIcon,
        styles.processed,
        styles.statusIcon,
        forChild && styles.forChild,
    )}
    >
        <Check />
    </div>
);

const ExpiredElement = ({ forChild }) => (
    <div className={cn(
        styles.rightIcon,
        styles.lightIcon,
        styles.statusIcon,
        forChild && styles.forChild,
    )}
    >
        <Expired />
    </div>
);

const SignElement = ({ title }) => (
    <div
        className={cn(styles.rightIcon, styles.signed)}
        title={title}
    >
        <SignedIcon />
    </div>
);

const DownloadElement = ({ register, domain, title }) => (
    <div className={styles.menu} title={title}>
        <DownloadButton register={register} domain={domain} />
    </div>
);

const contractorField: ContractorField = {
    Client: r => r.ClientORProviderShortName || r.ClientORProviderFullName,
    Factor: r => r.FactorShortName || r.FactorFullName,
    Debtor: r => r.DebtorORBuyerShortName || r.DebtorORBuyerShortName,
};

export const ListItem: ComponentType<ListItemType> = ({
    data: register,
    domain,
    queueStatus,
    isSelected,
    onClick,
    style,
    itemStyler,
    checkable,
    checkedRows,
    onCheck,
    checkedKeySelector,
}) => {
    const { t } = useTranslation('registersList');
    const isPlaceholder = queueStatus !== paginationStatuses.COMPLETE;
    const props = useMemo<ItemProps>(
        () => isPlaceholder ? {
            number: <GradientPlaceholder length={[9, 12]} />,
            date: <GradientPlaceholder length={11} />,
            amount: <GradientPlaceholder length={[10, 16]} />,
        } : {
            amount: getAmountField(register),
            date: getDateOr(register, 'CreateDate'),
            dates: checkAvailableDates(register) && t('dates', {
                from: getDateOr(register, 'RegisterStartDate'),
                to: getDateOr(register, 'RegisterEndDate'),
            }),
            number: register.Number,
            numberTooltip: register.Number,
            onRegisterClick: onClick,
            ...getCheckedProps({
                register,
                checkable,
                checkedRows,
                checkedKeySelector,
                onCheck,
            }),
            ...itemStyler && itemStyler(register),
        },
        [
            register,
            itemStyler,
            isPlaceholder,
            t,
            checkable,
            checkedRows,
            checkedKeySelector,
            onCheck,
            onClick,
        ],
    );
    const signIconKey = useMemo(
        () => register && getSignIconKey(register),
        [register],
    );

    return (
        <FocusableDiv
            className={cn(
                styles.item, {
                    [styles.placeholder]: isPlaceholder,
                    [styles.selected]: isSelected,
                    [styles.child]: props.isChild,
                },
            )}
            focusedClassName={!isSelected && styles.focused}
            onClick={props.onRegisterClick}
            style={style}
        >
            <div className={styles.line}>
                {props.checkable && (
                    <Checkbox
                        className={styles.checkbox}
                        markClassName={styles.checkboxMark}
                        checked={props.checked}
                        onChange={props.onCheck}
                        onClick={stopPropagation}
                    />
                )}
                <div className={styles.number} title={props.numberTooltip}>
                    {props.icon && (
                        <span className={styles.icon}>
                            {props.icon}
                        </span>
                    )}
                    <Text<ComponentProps<'span'>>
                        ellipsis
                        textType={props.isUnprocessed ? 'bolder' : 'regular'}
                        component='span'
                    >
                        {props.number}
                    </Text>
                </div>
                <Text component='div' textType='date' className={styles.date}>
                    {props.date}
                </Text>
            </div>
            {props.dates && !props.isChild && (
                <Text<ComponentProps<'div'>>
                    component='div'
                    textType='date'
                    textCompact
                >
                    {props.dates}
                </Text>
            )}
            {props.contractors && !props.isChild && props.contractors.map(type => {
                const contractor = contractorField[type](register);
                return (
                    <Text<ComponentProps<'div'>>
                        key={type}
                        component='div'
                        textType='light'
                        textCompact
                        ellipsis
                        title={contractor}
                    >
                        {contractor}
                    </Text>
                );
            })}
            <div className={cn(styles.line, styles.actionLine)}>
                {props.amount ? (
                    <Text textType='budget' component='div' className={styles.amount}>
                        {props.amount}
                    </Text>
                ) : props.description && (
                    <Text
                        component='div'
                        textType='info'
                        className={styles.description}
                        title={props.description as string}
                        ellipsis
                    >
                        {props.description}
                    </Text>
                )}
                <div className={styles.icons}>
                    {signIconKey && <SignElement title={t(signIconKey) as string} />}
                    {props.isForbidden && <ForbiddenElement forChild={props.isChild} />}
                    {props.isProcessed && <ProcessedElement forChild={props.isChild} />}
                    {!isPlaceholder && isSelected && (
                        <DownloadElement
                            register={register}
                            title={t('documentsAndSigns') as string}
                            domain={domain}
                        />
                    )}
                </div>
            </div>
        </FocusableDiv>
    );
};

export const DiscountListItem: ComponentType<DiscountListItemType> = ({
    data: register,
    queueStatus,
    isSelected,
    onClick,
    style,
    itemStyler,
}) => {
    const { t } = useTranslation('registersList');
    const isPlaceholder = queueStatus !== paginationStatuses.COMPLETE;
    const props = useMemo<DiscountItemProps>(
        () => isPlaceholder ? {
            number: <GradientPlaceholder length={[9, 12]} />,
            date: <GradientPlaceholder length={11} />,
        } : {
            date: getDateOr(register, 'CreateDate'),
            endDate: getDateOr(register, 'DiscountDate'),
            amount: `${formatCurrency(register.SupplyAmountForDiscount)} ₽`,
            discountAmountWithVat: `${formatCurrency(register.DiscountAmountWithVat)} ₽`,
            number: register.Number,
            numberTooltip: register.Number,
            onRegisterClick: onClick,
            ...itemStyler && itemStyler(register),
        },
        [
            register,
            itemStyler,
            isPlaceholder,
            onClick,
        ],
    );

    const signIconKey = useMemo(
        () => register && getSignIconKey(register),
        [register],
    );

    return (
        <FocusableDiv
            className={cn(
                styles.item, {
                    [styles.placeholder]: isPlaceholder,
                    [styles.selected]: isSelected,
                    [styles.child]: props.isChild,
                },
            )}
            focusedClassName={!isSelected && styles.focused}
            onClick={props.onRegisterClick}
            style={style}
        >
            <div className={styles.line}>
                <div className={styles.number} title={props.numberTooltip}>
                    {props.icon && (
                        <span className={styles.icon}>
                            {props.icon}
                        </span>
                    )}
                    <Text<ComponentProps<'span'>>
                        ellipsis
                        textType={props.isUnprocessed ? 'bolder' : 'regular'}
                        component='span'
                    >
                        {props.number}
                    </Text>
                </div>
                <Text component='div' textType='date' className={styles.date}>
                    {props.date}
                    {props.endDate &&
                        !props.isChild &&
                        register.RegisterTemplateCode === 'ReesterDiscount' && (
                        <div className={styles.endDate}>
                            <Text<ComponentProps<'div'>>
                                component='div'
                                textType='light'
                                textCompact
                            >
                                {t('endDate')}
                            </Text>
                            <Text<ComponentProps<'div'>>
                                component='div'
                                textType='date'
                                textCompact
                            >
                                {props.endDate}
                            </Text>
                        </div>
                    )}
                </Text>
            </div>
            {props.contractors && !props.isChild && props.contractors.map(type => {
                const contractor = contractorField[type](register);
                return (
                    <Text<ComponentProps<'div'>>
                        key={type}
                        component='div'
                        className={register &&
                            register.RegisterTemplateCode === 'ReesterDiscount' &&
                            styles.contractor
                        }
                        textType='light'
                        textCompact
                        ellipsis
                        title={contractor}
                    >
                        {contractor}
                    </Text>
                );
            })}
            <div className={cn(styles.line, styles.actionLine, styles.discountActionLine)}>
                {!props.isChild && props.discountAmountWithVat && (
                    <div className={styles.discountAmounts}>
                        <Text<ComponentProps<'div'>> textType='budget' component='div'>
                            {props.amount}
                        </Text>
                        <Text
                            component='div'
                            className={styles.discountAdditionalAmounts}
                            textType='light'
                            textCompact
                        >
                            <span className={styles.tariff}>{t('tariff')}</span>&nbsp;
                            <span>{props.discountAmountWithVat}</span>
                        </Text>
                    </div>
                )}
                {props.isChild && (
                    <Text<ComponentProps<'div'>>
                        component='div'
                        className={styles.discountAmounts}
                        textType='budget'
                    >
                        {props.amount}
                    </Text>
                )}
                {signIconKey && <SignElement title={t(signIconKey) as string} />}
                {props.isForbidden && <ForbiddenElement forChild={props.isChild} />}
                {props.isProcessed && <ProcessedElement forChild={props.isChild} />}
                {props.isExpired && <ExpiredElement forChild={props.isChild} />}
                {props.isReplying && <ReplyingElement forChild={props.isChild} />}
                {!isPlaceholder && isSelected && (
                    <DownloadElement
                        register={register}
                        title={t('documentsAndSigns') as string}
                        domain='discountRegisters'
                    />
                )}
            </div>
        </FocusableDiv>
    );
};
